// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useState, type FocusEvent } from 'react';

import { Combobox, useCombobox, NumberInput } from '@mantine/core';

import type { NumberInputWrappedProps } from 'components/Mantine/NumberInputWrapped';
import type { StrongOmit } from 'helpers/helper-types';
import { useUncontrolledNumber } from 'hooks/useUncontrolledNumber';

type Props = StrongOmit<NumberInputWrappedProps, 'value'> & {
  value?: number | null;
  options: readonly number[];
};

export const DropdownNumberInput = ({
  options,
  value: parentValue,
  defaultValue,
  onChange: parentOnChange,
  onBlur: parentOnBlur,
  onKeyDown,
  prefix,
  suffix,
  ...numberInputProps
}: Props) => {
  const combobox = useCombobox();

  const filterOptions = useCallback(
    (searchInput: string) => {
      const formattedOptions = options.map((n) => ({ value: n.toString(), label: n.toLocaleString() }));
      if (!searchInput) return formattedOptions;
      return formattedOptions.filter((option) => option.value.toLowerCase().includes(searchInput));
    },
    [options],
  );
  const [filteredOptions, setFilteredOptions] = useState(() => filterOptions(''));

  const { openDropdown, closeDropdown } = combobox;
  const onBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      closeDropdown();
      setFilteredOptions(filterOptions(''));
      parentOnBlur?.(e);
    },
    [parentOnBlur, setFilteredOptions, filterOptions, closeDropdown],
  );

  const { value, onChange, ...uncontrolledNumberProps } = useUncontrolledNumber({
    value: parentValue,
    defaultValue,
    onChange: parentOnChange,
    onBlur,
    onKeyDown,
  });

  return (
    <Combobox
      onOptionSubmit={(v) => {
        onChange(parseFloat(v));
        setFilteredOptions(filterOptions(''));
        closeDropdown();
      }}
      store={combobox}
    >
      <Combobox.Target>
        <NumberInput
          prefix={prefix}
          suffix={suffix}
          value={value}
          onChange={(e) => {
            onChange(e);
            setFilteredOptions(filterOptions(e.toString()));
            openDropdown();
          }}
          onClick={() => openDropdown()}
          onFocus={() => openDropdown()}
          {...numberInputProps}
          {...uncontrolledNumberProps}
        />
      </Combobox.Target>
      {filteredOptions.length > 0 && (
        <Combobox.Dropdown>
          <Combobox.Options>
            {filteredOptions.map((option) => (
              <Combobox.Option key={option.value} value={option.value}>
                {prefix}
                {option.label}
                {suffix}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Combobox.Dropdown>
      )}
    </Combobox>
  );
};
